import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Trans } from '@lingui/macro';
import { useMediaQuery, useTheme } from '@mui/material';
import { ListHeaderWrapper } from '../lists/ListHeaderWrapper';
import { ListColumn } from '../lists/ListColumn';
import { DASHBOARD_LIST_COLUMN_WIDTHS } from '../../utils/dashboardSortUtils';
import { ListHeaderTitle } from '../lists/ListHeaderTitle';
import { ListButtonsColumn } from '../../modules/dashboard/lists/ListButtonsColumn';
import {
  EControlPositionType,
  EPoolType,
  EPositionType,
  IEnrichedUniswapPosition,
} from '../../types/uniswapTokens';
import { EMarketName } from '../../ui-config/marketsConfig';
import { DashboardContentNoData } from '../../modules/dashboard/DashboardContentNoData';
import {
  IControlPositionTypeFilter,
  IPoolMarketFilter,
  IPoolTypeFilter,
} from '../../modules/leverage/lists/ToLeveragePositionsList/types';
import { handleSortUniswapPositions } from '../../utils/yldrSortUtils';
import { PositionsListItem } from './components/PositionsListItem';
import { IEstNetAprState } from './types';
import { AlmPositionsListItem } from './components/AlmPositionsListItem';
import { PoolsListItem } from './components/PoolsListItem';
import { PoolsListItemMobile } from './components/PoolsListItemMobile';
import { PositionsListItemMobile } from './components/PositionsListItemMobile';
import { AlmPositionsListItemMobile } from './components/AlmPositionsListItemMobile';

export interface IUniswapPositionsListProps {
  positions: IEnrichedUniswapPosition[];
  head: { title: JSX.Element, sortKey: string; }[];
  actions: (args: {
    position: IEnrichedUniswapPosition;
    tokenId?: string;
    positionAddress?: string;
    isEmptyPosition?: boolean;
    uniswapLink?: string;
  }) => React.ReactNode;
  columnsType: 'lending' | 'leverage' | 'leveraged';
  typeFilter?: IPoolTypeFilter;
  marketFilter?: IPoolMarketFilter;
  controlPositionFilter?: IControlPositionTypeFilter;
}

const columnSizes: Record<string, 'small' | 'large' | 'big' | 'medium' | undefined> = {
  ['lending']: 'small',
  ['leverage']: 'big',
  ['leveraged']: 'big',
};

export const PositionsList = ({
  positions,
  head,
  actions,
  columnsType,
  typeFilter,
  marketFilter,
  controlPositionFilter,
}: IUniswapPositionsListProps) => {
  const theme = useTheme();
  const downToXSM = useMediaQuery(theme.breakpoints.down('xsm'));

  const [estNetAprs, setEstNetAprs] = useState<IEstNetAprState[]>([]);

  const [sortName, setSortName] = useState('maxApr');
  const [sortDesc, setSortDesc] = useState(false);

  const filteredItems = useMemo(
    () => positions
      .filter(
        (item) =>
          !typeFilter ||
          typeFilter?.value === EPoolType.all ||
          typeFilter?.value === item.poolType
      )
      .filter(
        (item) =>
          !controlPositionFilter ||
          controlPositionFilter?.value === EControlPositionType.Unknown ||
          controlPositionFilter?.value === EControlPositionType.AlmPosition && item.almMarketName ||
          controlPositionFilter?.value === EControlPositionType.ManualPosition && !item.almMarketName
      )
      .filter(
        (item) =>
          !marketFilter ||
          marketFilter?.value === EMarketName.None ||
          marketFilter?.value === item.marketName
      ),
    [positions, typeFilter, marketFilter, controlPositionFilter]
  );

  const setEstNetAprHandler = (newState: IEstNetAprState) => {
    setEstNetAprs((prevState) => ([
      ...prevState.filter((item) => item.tokenId !== newState.tokenId),
      newState,
    ]));
  };

  const sortedItems = handleSortUniswapPositions({
    sortDesc,
    sortName,
    positions: [...filteredItems].map((item) => {
      const isInnerLoading = estNetAprs.some((i) => i.isLoading);
      if (item.type === EPositionType.Pool || isInnerLoading) return item;
      else {
        const estNetApr = estNetAprs.find((i) => i.tokenId === item.tokenId)
        return {
          ...item,
          maxApr: estNetApr?.estNetApr || item.maxApr
        }
      }
    })
  });

  useEffect(() => {
    if (downToXSM) {
      setSortName('maxApr');
      setSortDesc(false);
    }
  }, [downToXSM]);

  return sortedItems.length ? (
    <>
      {!downToXSM && (
        <ListHeaderWrapper>
          {head.map((col) => (
            <ListColumn
              isRow={col.sortKey === 'pool'}
              maxWidth={col.sortKey === 'pool' ? DASHBOARD_LIST_COLUMN_WIDTHS.POOL : undefined}
              minWidth={col.sortKey === 'pool' ? DASHBOARD_LIST_COLUMN_WIDTHS.POOL : undefined}
              key={col.sortKey}
            >
              <ListHeaderTitle
                sortName={sortName}
                sortDesc={sortDesc}
                setSortName={setSortName}
                setSortDesc={setSortDesc}
                sortKey={col.sortKey}
              >
                {col.title}
              </ListHeaderTitle>
            </ListColumn>
          ))}
          <ListButtonsColumn
            size={columnSizes[columnsType] || 'small'}
            isColumnHeader
          />
        </ListHeaderWrapper>
      )}
      {sortedItems.map((item, index) => (
        <Fragment key={`${item.tokenId}-${index}`}>
          {downToXSM ? (
            <>
              {item.type === EPositionType.Pool && (
                <PoolsListItemMobile
                  position={item}
                  actions={actions}
                  columnsType={columnsType}
                />
              )}
              {item.controlType === EControlPositionType.ManualPosition && (
                <PositionsListItemMobile
                  position={item}
                  actions={actions}
                  columnsType={columnsType}
                />
              )}
              {item.controlType === EControlPositionType.AlmPosition && (
                <AlmPositionsListItemMobile
                  position={item}
                  actions={actions}
                  columnsType={columnsType}
                />
              )}
            </>
          ) : (
            <>
              {item.type === EPositionType.Pool && (
                <PoolsListItem
                  position={item}
                  actions={actions}
                  columnsType={columnsType}
                  setEstNetAprHandler={
                    item.type !== EPositionType.Pool && (columnsType === 'leverage' || columnsType === 'leveraged')
                      ? setEstNetAprHandler
                      : undefined
                  }
                />
              )}
              {item.controlType === EControlPositionType.ManualPosition && (
                <PositionsListItem
                  position={item}
                  actions={actions}
                  columnsType={columnsType}
                  setEstNetAprHandler={
                    item.type !== EPositionType.Pool && (columnsType === 'leverage' || columnsType === 'leveraged')
                      ? setEstNetAprHandler
                      : undefined
                  }
                />
              )}
              {item.controlType === EControlPositionType.AlmPosition && (
                <AlmPositionsListItem
                  position={item}
                  actions={actions}
                  columnsType={columnsType}
                  setEstNetAprHandler={
                    item.type !== EPositionType.Pool && (columnsType === 'leverage' || columnsType === 'leveraged')
                      ? setEstNetAprHandler
                      : undefined
                  }
                />
              )}
            </>
          )}
        </Fragment>
      ))}
    </>
  ) : (
    <DashboardContentNoData text={<Trans>Nothing supplied yet</Trans>}/>
  );
};
